import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowBack } from '@mui/icons-material';
import styles from './MyInformation.module.css';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { getUserId } from '../utils/userId';
import onboardingQuestions from '../data/onboardingQuestions.json';

interface OnboardingAnswer {
  questionId: string;
  value: any;
}

const MyInformation: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';
  
  // 원본 데이터, 수정 중인 데이터, 수정 모드 상태
  const [originalAnswers, setOriginalAnswers] = useState<{ [key: string]: any }>({});
  const [answers, setAnswers] = useState<{ [key: string]: any }>({});
  const [isEditing, setIsEditing] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userId = getUserId();
        if (!userId) {
          console.error('User ID is missing');
          setLoading(false);
          return;
        }

        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('Firestore에서 가져온 원본 user data:', userData);
          const savedResponses = userData.responses || {};
          console.log('State에 저장될 savedResponses 객체:', savedResponses);
        
          setAnswers(savedResponses);
          setOriginalAnswers(savedResponses);
        }
        else {
          console.log('해당 ID의 사용자 문서를 찾을 수 없습니다!');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    if (loading) return;

    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleScroll = () => {
      contentElement.classList.remove(styles.scrollHidden);
      
      clearTimeout(scrollTimerRef.current);

      scrollTimerRef.current = setTimeout(() => {
        contentElement.classList.add(styles.scrollHidden);
      }, 400); 
    };

    contentElement.addEventListener('scroll', handleScroll);
    
    contentElement.classList.add(styles.scrollHidden);

    return () => {
      contentElement.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimerRef.current);
    };
  }, [loading]);

  const handleBack = () => {
    navigate(`${prefix}/check`);
  };

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // 저장 핸들러: 저장 후 '조회 모드'로 변경
  const handleSave = async () => {
    try {
      const userId = getUserId();
      if (!userId) {
        console.error('User ID is missing');
        return;
      }

      await updateDoc(doc(db, 'users', userId), {
        responses: answers, 
        updatedAt: new Date()
      });

      setOriginalAnswers(answers); // 저장된 데이터를 새 원본으로 설정
      setIsEditing(false); // 조회 모드로 전환

      // 저장 성공 메시지
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000); 
      // 

    } catch (error) {
      console.error('Error saving user data:', error);
    }
  };

  // 수정 모드 활성화
  const handleEdit = () => {
    setIsEditing(true);
  };

  // 수정 취소
  const handleCancel = () => {
    setAnswers(originalAnswers); // 원본 데이터로 복구
    setIsEditing(false);
  };

  // 선택된 값의 텍스트 라벨을 가져오는 헬퍼 함수
  const getOptionLabel = (question: any, value: any) => {
    if (!question.options) return value;
    const option = question.options.find((opt: any) => opt.value === value);
    // optionTextKey가 있는 경우 번역, 없으면 value
    return option ? (option.optionTextKey ? t(option.optionTextKey) : option.value) : value;
  };

  // '조회 모드'에서 답변을 텍스트로 렌더링
  const renderQuestionView = (question: any) => {
    const value = answers[question.id];
    let displayValue = '';

    if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) {
      displayValue = t('no_answer', '응답 없음');
    } else {
      switch (question.type) {
        case 'year_picker':
        case 'text_input':
          displayValue = value;
          break;
        case 'single_choice':
        case 'dropdown':
          displayValue = getOptionLabel(question, value);
          break;
        case 'multi_choice':
          displayValue = value.map((v: any) => getOptionLabel(question, v)).join(', ');
          break;
        default:
          displayValue = 'N/A';
      }
    }
    return <p className={styles.answerText}>{displayValue}</p>;
  };

  // '수정 모드'에서 입력 폼을 렌더링 (이름 변경: renderQuestion -> renderQuestionInput)
  const renderQuestionInput = (question: any) => {
    switch (question.type) {
      case 'year_picker':
        return (
            <select
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, parseInt(e.target.value))}
              className={styles.selectInput}
            >
              <option value="">{t('select_year')}</option>
              {question.options.map((option: any) => (
                <option key={option.value} value={option.value}>
                  {option.value}
                </option>
              ))}
            </select>
        );
      case 'single_choice':
        return (
            <div className={styles.optionsContainer}>
              {question.options.map((option: any) => (
                <button
                  key={option.value}
                  className={`${styles.optionButton} ${answers[question.id] === option.value ? styles.selected : ''}`}
                  onClick={() => handleAnswerChange(question.id, option.value)}
                >
                  {t(option.optionTextKey) as unknown as string}
                </button>
              ))}
            </div>
        );
      case 'dropdown':
        return (
            <select
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              className={styles.selectInput}
            >
              <option value="">{t('select_option')}</option>
              {question.options.map((option: any) => (
                <option key={option.value} value={option.value}>
                  {t(option.optionTextKey) as unknown as string}
                </option>
              ))}
            </select>
        );
      case 'multi_choice':
        return (
            <div className={styles.optionsContainer}>
              {question.options.map((option: any) => (
                <button
                  key={option.value}
                  className={`${styles.optionButton} ${answers[question.id]?.includes(option.value) ? styles.selected : ''}`}
                  onClick={() => {
                    const currentAnswers = answers[question.id] || [];
                    const newAnswers = currentAnswers.includes(option.value)
                      ? currentAnswers.filter((v: any) => v !== option.value)
                      : [...currentAnswers, option.value];
                    handleAnswerChange(question.id, newAnswers);
                  }}
                >
                  {t(option.optionTextKey) as unknown as string}
                </button>
              ))}
            </div>
        );
      case 'text_input':
        return (
            <input
              type="text"
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              className={styles.textInput}
              placeholder={t('enter_text')}
            />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={handleBack}>
            <ArrowBack />
          </button>
          <span className={styles.headerText}>{t('my_information')}</span>
        </div>
        <div className={styles.content}>
          <div className={`${styles.contentScroll} ${styles.loadingContainer}`}>
            <div className={styles.spinner}></div>
            <div className={styles.loadingText}>
              {t('loading', '불러오는 중...')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // JSX 구조 변경 (StrokeCheck.tsx와 유사하게)
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t('my_information')}</span>
      </div>

      <div className={styles.content}>
        <div className={styles.contentScroll} ref={contentRef}>
          <div className={styles.questionsList}>
            {onboardingQuestions.questions.map((question) => (
              <div key={question.id} className={styles.questionContainer}>
                <h3 className={styles.questionTitle}>{t(question.questionTextKey) as unknown as string}</h3>
                {isEditing
                  ? renderQuestionInput(question) // 수정 모드
                  : renderQuestionView(question)   // 조회 모드
                }
              </div>
            ))}
          </div>
        </div>
        {showSuccess && (
          <div className={styles.successToast}>
            {t('save_success', '저장이 완료되었습니다.')}
          </div>
        )}
      </div>
      
      {/* 하단 버튼: 상태에 따라 다르게 렌더링 */}
      {!isEditing ? (
        <button className={styles.editButton} onClick={handleEdit}>
          {t('edit_info', '정보 수정')}
        </button>
      ) : (
        <div className={styles.buttonContainer}>
          <button className={styles.cancelButton} onClick={handleCancel}>
            {t('cancel', '취소')}
          </button>
          <button className={styles.saveButton} onClick={handleSave}>
            {t('save_changes', '저장하기')}
          </button>
        </div>
      )}
      
    </div>
  );
};

export default MyInformation;